import { useState, useSyncExternalStore } from 'react';
import type { NurseTask, Person } from './api';

/** "Mona A." — the station speaks in first names and an initial. */
export function shortName(p: Pick<Person, 'firstName' | 'lastName'> | null | undefined): string {
  if (!p) return '';
  const first = (p.firstName ?? '').trim();
  const last = (p.lastName ?? '').trim();
  return last ? `${first} ${last[0].toUpperCase()}.` : first;
}

/** "Dr. Ahmed". */
export function drName(p: Pick<Person, 'firstName'> | null | undefined): string {
  return p?.firstName ? `Dr. ${p.firstName.trim()}` : '';
}

/** "7:42" — a clock time on today's list needs no date and no am/pm. */
export function clock(iso: string | Date | null | undefined, withMeridiem = false): string {
  if (!iso) return '';
  const d = new Date(iso);
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  const pm = h >= 12;
  h = h % 12 || 12;
  return withMeridiem ? `${h}:${m} ${pm ? 'pm' : 'am'}` : `${h}:${m}`;
}

/** "just now" · "6 min ago" · "7:42". */
export function sinceWhen(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '';
  const min = Math.floor((now - new Date(iso).getTime()) / 60000);
  if (min < 2) return 'just now';
  if (min < 60) return `${min} min ago`;
  return clock(iso);
}

/** "12:00" · "1:02:05". Never negative — a clock at zero says 0:00. */
export function mmss(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve'];
/** "nine" — counts a person would say aloud. */
export function numberWord(n: number): string {
  return WORDS[n] ?? String(n);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "24 Aug" from a `YYYY-MM-DD` date column, read as a calendar day. */
export function dayMonth(ymd: string | null | undefined): string {
  if (!ymd) return '';
  const [, m, d] = ymd.slice(0, 10).split('-').map(Number);
  return `${d} ${MONTHS[m - 1] ?? ''}`.trim();
}

export function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/* ONE CLOCK for every nurse screen. The row and the pane used to tick on
   two intervals and could read 19:58 and 19:59 at the same instant; now every
   subscriber reads the same second from one shared ticker. */
let tick = Date.now();
const listeners = new Set<() => void>();
let timer: number | null = null;
function subscribe(fn: () => void) {
  listeners.add(fn);
  if (timer === null) {
    tick = Date.now();
    timer = window.setInterval(() => {
      tick = Date.now();
      listeners.forEach((l) => l());
    }, 1000);
  }
  return () => {
    listeners.delete(fn);
    if (!listeners.size && timer !== null) {
      window.clearInterval(timer);
      timer = null;
    }
  };
}
const noop = () => () => {};

/** The shared second — re-renders every second while `active`. */
export function useNow(active: boolean): number {
  const [frozen] = useState(() => Date.now());
  const now = useSyncExternalStore(active ? subscribe : noop, () => (active ? tick : frozen));
  return now;
}

export function secondsLeft(task: Pick<NurseTask, 'dueAt'>, now: number): number | null {
  if (!task.dueAt) return null;
  return (new Date(task.dueAt).getTime() - now) / 1000;
}

/** "Dilate — Mona A." / "Consent — minor procedure · Omar K." / "Chaperone — Room 2". */
export function taskLabel(t: NurseTask): string {
  const who = t.patient ? shortName(t.patient) : t.room ?? '';
  if (!who) return t.title;
  return t.title.includes(' — ') ? `${t.title} · ${who}` : `${t.title} — ${who}`;
}

const KIND_LABEL: Record<string, string> = {
  dilate: 'Dilate', vitals: 'Vitals', injection: 'Injection', observe: 'Observe',
  consent: 'Consent', chaperone: 'Chaperone', dressing: 'Dressing',
};

/** "Injection · Omar K." — what the task is, then who. */
export function execTitle(t: NurseTask): string {
  const label = KIND_LABEL[t.kind] ?? t.title;
  const who = t.patient ? shortName(t.patient) : t.room ?? '';
  return who ? `${label} · ${who}` : label;
}

export function kindLabel(t: NurseTask): string {
  return KIND_LABEL[t.kind] ?? t.title;
}

export const CANT_REASONS: Array<{ key: string; label: string; does: string }> = [
  { key: 'declined', label: 'Patient declined', does: 'task closes · doctor told' },
  { key: 'left', label: 'Patient left / not here', does: 'task closes · doctor told' },
  { key: 'out_of_stock', label: 'Out of stock', does: 'closes · also flags the shelf' },
  { key: 'consent_refused', label: 'Consent refused', does: 'task closes · doctor told · the reason is kept' },
  { key: 'not_appropriate', label: 'Not appropriate now — see note', does: 'closes with her line attached' },
  { key: 'other', label: 'Other — write the reason', does: 'closes with your line attached — the line is required' },
];

/** What a reason does for THIS task — Consent refused only blocks an order the task is linked to. */
export function cantDoes(r: { key: string; does: string }, task: Pick<NurseTask, 'orderLinked'>): string {
  return r.key === 'consent_refused' && task.orderLinked
    ? 'BLOCKS the procedure order — nothing performed, nothing billed'
    : r.does;
}

export function cantLabel(key: string | null): string {
  return CANT_REASONS.find((r) => r.key === key)?.label ?? 'can’t';
}

/* The station's short forms for the band of conditions — "DM2", "HTN". A
   condition with no short form is shown as written (the chip ellipsises). */
const SHORT_FORMS: Array<[RegExp, string]> = [
  [/type\s*2\s*diabetes|diabetes mellitus,?\s*type\s*(2|ii)|\bt2dm\b|\bdm2\b/i, 'DM2'],
  [/type\s*1\s*diabetes|diabetes mellitus,?\s*type\s*(1|i)\b|\bt1dm\b/i, 'DM1'],
  [/hypertension|\bhtn\b/i, 'HTN'],
  [/hyperlipid|dyslipid|hypercholesterol/i, 'HLD'],
  [/chronic kidney disease|\bckd\b/i, 'CKD'],
  [/coronary artery disease|ischemic heart|ischaemic heart|\bcad\b/i, 'CAD'],
  [/atrial fibrillation/i, 'AF'],
  [/heart failure/i, 'HF'],
  [/chronic obstructive|\bcopd\b/i, 'COPD'],
  [/asthma/i, 'Asthma'],
  [/hypothyroid/i, 'Hypothyroid'],
  [/hyperthyroid/i, 'Hyperthyroid'],
  [/glaucoma/i, 'Glaucoma'],
  [/gastro-?o?esophageal reflux|\bgerd\b/i, 'GERD'],
  [/obesity/i, 'Obesity'],
];
export function shortCondition(label: string): string {
  const hit = SHORT_FORMS.find(([re]) => re.test(label));
  return hit ? hit[1] : label.replace(/\s*\([^)]*\)\s*$/, '').trim();
}
